import React, { useEffect, useState, useMemo } from 'react';
import { 
  Drawer, 
  Form, 
  Input, 
  Button, 
  Space, 
  Typography, 
  Table, 
  Select, 
  Card, 
  Row, 
  Col,
  Tag
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { accountsAPI } from '../services/api';
import Loader from '../components/Loader';
import './Accounts.css';

const { Title, Text } = Typography;
const { Search } = Input;

interface Account {
  _id: string;
  phoneNumber: string;
  accountName: string;
  status: string;
  qrCode: string | null;
  lastSync: string | null;
  groupsCount: number;
  membersCount: number;
}

const Accounts: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ phoneNumber: '+91', accountName: '' });
  
  // Filters and pagination
  const [searchText, setSearchText] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [groupsFilter, setGroupsFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await accountsAPI.getAll();
      setAccounts(response.data);
      setError(null);
      return response.data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch accounts');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const validatePhoneNumber = (phone: string): boolean => {
    // Must start with +91 and have exactly 10 digits after
    const phoneRegex = /^\+91\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleCreateAccount = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    // Validate phone number
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError('Phone number must start with +91 followed by exactly 10 digits (e.g., +919876543210)');
      return;
    }
    
    try {
      await accountsAPI.create(formData);
      setShowModal(false);
      setFormData({ phoneNumber: '+91', accountName: '' });
      setError(null);
      fetchAccounts();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create account');
    }
  };


  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await accountsAPI.delete(id);
        fetchAccounts();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to delete account');
      }
    }
  };

  // Filter accounts based on search and filters
  const filteredAccounts = useMemo(() => {
    let filtered = [...accounts];

    // Search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(account => 
        account.accountName.toLowerCase().includes(searchLower) ||
        account.phoneNumber.includes(searchText)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(account => account.status === statusFilter);
    }

    // Groups count filter
    if (groupsFilter !== 'all') {
      const [min, max] = groupsFilter.split('-').map(Number);
      if (max) {
        filtered = filtered.filter(account => 
          account.groupsCount >= min && account.groupsCount <= max
        );
      } else {
        filtered = filtered.filter(account => account.groupsCount >= min);
      }
    }

    return filtered;
  }, [accounts, searchText, statusFilter, groupsFilter]);

  // Pagination
  const paginatedAccounts = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAccounts.slice(start, end);
  }, [filteredAccounts, currentPage, pageSize]);

  // Get status tag color
  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string }> = {
      connected: { color: 'success', text: 'Connected' },
      connecting: { color: 'processing', text: 'Connecting' },
      disconnected: { color: 'default', text: 'Disconnected' },
      error: { color: 'error', text: 'Error' },
    };
    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // Table columns
  const columns = [
    {
      title: 'Account Name',
      dataIndex: 'accountName',
      key: 'accountName',
      sorter: (a: Account, b: Account) => a.accountName.localeCompare(b.accountName),
      render: (text: string) => <Text strong>{text}</Text>,
    },
    {
      title: 'Phone Number',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      sorter: (a: Account, b: Account) => a.phoneNumber.localeCompare(b.phoneNumber),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Connected', value: 'connected' },
        { text: 'Connecting', value: 'connecting' },
        { text: 'Disconnected', value: 'disconnected' },
        { text: 'Error', value: 'error' },
      ],
      onFilter: (value: any, record: Account) => record.status === value,
      render: (status: string) => getStatusTag(status),
    },
    {
      title: 'Groups',
      dataIndex: 'groupsCount',
      key: 'groups',
      sorter: (a: Account, b: Account) => a.groupsCount - b.groupsCount,
      render: (count: number) => <Tag color="#0e4842">{count}</Tag>,
    },
    {
      title: 'Members',
      dataIndex: 'membersCount',
      key: 'members',
      sorter: (a: Account, b: Account) => a.membersCount - b.membersCount,
      render: (count: number) => <Tag color="green">{count}</Tag>,
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSync',
      key: 'lastSync',
      sorter: (a: Account, b: Account) => {
        const aTime = a.lastSync ? new Date(a.lastSync).getTime() : 0;
        const bTime = b.lastSync ? new Date(b.lastSync).getTime() : 0;
        return aTime - bTime;
      },
      render: (lastSync: string | null) => 
        lastSync ? new Date(lastSync).toLocaleString() : <Text type="secondary">Never</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Account) => (
        <Space>
          <Button
            danger
            onClick={() => handleDelete(record._id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  if (loading && accounts.length === 0) {
    return <Loader fullScreen text="Loading accounts..." />;
  }

  return (
    <div className="accounts-page">
      <div className="page-header">
        <Title level={2}>WhatsApp Accounts</Title>
        <Space>
          <Button type="primary" onClick={() => setShowModal(true)}>
            + Add Account
          </Button>
        </Space>
      </div>

      {error && <div className="error">{error}</div>}

      <Card>
        {/* Filters Section */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search by account name or phone number"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setCurrentPage(1);
              }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by Status"
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              size="large"
              style={{ width: '100%' }}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setCurrentPage(1);
              }}
              options={[
                { label: 'All Statuses', value: 'all' },
                { label: 'Connected', value: 'connected' },
                { label: 'Connecting', value: 'connecting' },
                { label: 'Disconnected', value: 'disconnected' },
                { label: 'Error', value: 'error' },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="Filter by Groups Count"
              showSearch={false}
              size="large"
              style={{ width: '100%' }}
              value={groupsFilter}
              onChange={(value) => {
                setGroupsFilter(value);
                setCurrentPage(1);
              }}
              options={[
                { label: 'All Sizes', value: 'all' },
                { label: '0 groups', value: '0-0' },
                { label: '1-10 groups', value: '1-10' },
                { label: '11-50 groups', value: '11-50' },
                { label: '51-100 groups', value: '51-100' },
                { label: '100+ groups', value: '100' },
              ]}
            />
          </Col>
        </Row>

        {/* Table */}
        {accounts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Text type="secondary">No accounts found. Add your first WhatsApp account to get started.</Text>
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={paginatedAccounts}
            rowKey="_id"
            loading={loading}
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: filteredAccounts.length,
              showSizeChanger: true,
              showTotal: (total, range) => 
                `${range[0]}-${range[1]} of ${total} accounts`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Card>

      <Drawer
        title="Add New Account"
        placement="right"
        onClose={() => {
          setShowModal(false);
          setFormData({ phoneNumber: '+91', accountName: '' });
        }}
        open={showModal}
        width={400}
      >
        <Form
          key={showModal ? 'open' : 'closed'}
          layout="vertical"
          onFinish={() => {
            handleCreateAccount();
          }}
          initialValues={formData}
        >
          <Form.Item
            label="Account Name"
            name="accountName"
            rules={[{ required: true, message: 'Please enter account name' }]}
          >
            <Input
              placeholder="e.g., Marketing Account 1"
              onChange={(e) =>
                setFormData({ ...formData, accountName: e.target.value })
              }
            />
          </Form.Item>
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Please enter phone number' },
              { 
                pattern: /^\+91\d{10}$/, 
                message: 'Phone number must start with +91 followed by exactly 10 digits (e.g., +919876543210)' 
              }
            ]}
          >
            <Input
              placeholder="+919876543210"
              maxLength={13}
              onChange={(e) => {
                let value = e.target.value;
                // Auto-format: ensure +91 prefix
                if (value && !value.startsWith('+91')) {
                  // Remove any leading 0 or 91
                  value = value.replace(/^(0|91)/, '');
                  if (value && !value.startsWith('+')) {
                    value = '+91' + value;
                  }
                }
                setFormData({ ...formData, phoneNumber: value });
              }}
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12, marginTop: -16, marginBottom: 16, display: 'block' }}>
            Format: +91 followed by 10 digits (e.g., +919876543210)
          </Text>
          <Form.Item>
            <Space className="w-full" style={{ justifyContent: 'flex-end' }}>
              <Button onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit">
                Create Account
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Drawer>

    </div>
  );
};

export default Accounts;

